import React from 'react';
import { withStyles } from '@material-ui/styles';
import { Button } from '@material-ui/core';
import flowRight from 'lodash.flowright';
import clsx from 'clsx';
import withSettings from './withSettings';
import DEFAULT_SETTINGS from '../consts/frontendSettings';
import Optional from './optional';

export const getPageNumbers = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  if (current <= 3) {
    return [0, 1, 2, 3, 4, 'ellipsis-end', total - 1];
  }
  if (current >= total - 4) {
    return [
      0,
      'ellipsis-start',
      total - 5,
      total - 4,
      total - 3,
      total - 2,
      total - 1
    ];
  }
  return [
    0,
    'ellipsis-start',
    current - 1,
    current,
    current + 1,
    'ellipsis-end',
    total - 1
  ];
};

const styles = {
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '12px',
    width: '100%',
    gap: '4px'
  },
  pageButton: {
    minWidth: '32px',
    height: '32px',
    padding: '0 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid #EBEDF2',
    backgroundColor: '#fff',
    color: '#595d6e',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    outline: 'none',
    userSelect: 'none',
    transition: 'all 0.2s ease',
    '&:hover:not($pageButtonDisabled):not($pageButtonActive)': {
      backgroundColor: '#f4f5f8',
      borderColor: '#d1d3e0'
    },
    '&:focus': {
      outline: 'none'
    }
  },
  pageButtonActive: {
    backgroundColor: '#5d78ff',
    borderColor: '#5d78ff',
    color: '#ffffff',
    fontWeight: 600,
    cursor: 'default'
  },
  pageButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed'
  },
  pageEllipsis: {
    minWidth: '24px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#a7abc3',
    fontSize: '13px'
  }
};

const Pagination = ({
  classes,
  currentPage = 0,
  page,
  totalPages = 0,
  onPageChange,
  loading = false,
  disabled = false,
  settings,
  className,
  style,
  showFirstLast = true,
  showPrevNext = true
}) => {
  const activePage = Optional(page).or(currentPage);

  if (totalPages <= 1) {
    return null;
  }

  const activeSettings = Optional(settings).or(DEFAULT_SETTINGS);
  const activeBgColor =
    activeSettings?.colors?.modal_header ||
    DEFAULT_SETTINGS.colors.modal_header;
  const activeTextColor =
    activeSettings?.colors?.modal_header_text ||
    DEFAULT_SETTINGS.colors.modal_header_text;

  const handlePageClick = newPage => {
    if (
      newPage < 0 ||
      newPage >= totalPages ||
      newPage === activePage ||
      loading ||
      disabled
    ) {
      return;
    }
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const isPrevDisabled = activePage === 0 || loading || disabled;
  const isNextDisabled = activePage === totalPages - 1 || loading || disabled;

  return (
    <div className={clsx(classes.paginationContainer, className)} style={style}>
      {showFirstLast && (
        <Button
          className={clsx(classes.pageButton, {
            [classes.pageButtonDisabled]: isPrevDisabled
          })}
          disabled={isPrevDisabled}
          onClick={() => handlePageClick(0)}
        >
          <i className="far fa-chevron-double-left" />
        </Button>
      )}
      {showPrevNext && (
        <Button
          className={clsx(classes.pageButton, {
            [classes.pageButtonDisabled]: isPrevDisabled
          })}
          disabled={isPrevDisabled}
          onClick={() => handlePageClick(activePage - 1)}
        >
          <i className="fas fa-chevron-left" />
        </Button>
      )}
      {getPageNumbers(activePage, totalPages).map((item, index) => {
        if (typeof item === 'string') {
          return (
            <span key={`${item}-${index}`} className={classes.pageEllipsis}>
              ...
            </span>
          );
        }
        const isActive = item === activePage;
        return (
          <Button
            key={item}
            style={
              isActive
                ? {
                    backgroundColor: activeBgColor,
                    borderColor: activeBgColor,
                    color: activeTextColor
                  }
                : void 0
            }
            className={clsx(classes.pageButton, {
              [classes.pageButtonActive]: isActive,
              [classes.pageButtonDisabled]: loading || disabled
            })}
            disabled={loading || disabled}
            onClick={() => handlePageClick(item)}
          >
            {item + 1}
          </Button>
        );
      })}
      {showPrevNext && (
        <Button
          className={clsx(classes.pageButton, {
            [classes.pageButtonDisabled]: isNextDisabled
          })}
          disabled={isNextDisabled}
          onClick={() => handlePageClick(activePage + 1)}
        >
          <i className="fas fa-chevron-right" />
        </Button>
      )}
      {showFirstLast && (
        <Button
          className={clsx(classes.pageButton, {
            [classes.pageButtonDisabled]: isNextDisabled
          })}
          disabled={isNextDisabled}
          onClick={() => handlePageClick(totalPages - 1)}
        >
          <i className="far fa-chevron-double-right" />
        </Button>
      )}
    </div>
  );
};

export default flowRight(withSettings, withStyles(styles))(Pagination);
